import { useEffect, useState } from "react";
import { Alert, Button, Card, CircularProgress, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "src/app/main/auth/AuthContext";
import { setSessionRedirectUrl } from "@fuse/core/FuseAuthorization/sessionRedirectUrl";
import petService, { PetRemovalDetails } from "@fuse/services/petService";
import RemovePetDialog from "./RemovePetDialog";

export default function CampaignRemovePetPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth() as { isAuthenticated: boolean };
    const [details, setDetails] = useState<PetRemovalDetails | null>(null);
    const [error, setError] = useState("");
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let current = true;
        setDetails(null); setError("");
        if (!id) { setError("No pet record was selected."); return; }
        if (!isAuthenticated) {
            setSessionRedirectUrl(`/client/pet/remove/${encodeURIComponent(id)}`);
            navigate("/sign-in", { replace: true });
            return;
        }
        petService.getOwnedRemovalDetails(id).then(result => {
            if (current) setDetails(result);
        }).catch(e => {
            if (current) setError(e?.response?.status === 404
                ? "This record is not available for removal by this account. Sign in as its current owner or contact Peeva support."
                : e?.response?.data?.errors?.[0] || e?.message || "Unable to verify this record. Please try again.");
        });
        return () => { current = false; };
    }, [id, isAuthenticated, attempt, navigate]);

    // An audit ID is required before displaying success. Login/page load never submits.
    async function remove(petId: string, reason: string) {
        const result = await petService.removeOwnedPet(petId, reason) as { id?: string };
        if (typeof result?.id !== "string" || !result.id.trim())
            throw new Error("Removal was not confirmed. Please retry or contact Peeva support.");
        return result;
    }
    const close = () => navigate("/client/pet");
    if (error) return <Card className="m-20 p-24">
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => setAttempt(value => value + 1)}>Retry</Button>
        <Button onClick={close}>Back to my pets</Button>
    </Card>;
    if (!details) return <div className="p-24" role="status"><CircularProgress aria-label="Verifying pet ownership" /></div>;
    if (details.isRemoved) return <Card className="m-20 p-24">
        <Typography variant="h6">Removal already recorded for {details.petName}.</Typography>
        <Typography>Reference: {details.auditId}</Typography>
        <Button onClick={close}>Back to my pets</Button>
    </Card>;
    return <RemovePetDialog key={details.petId} petId={details.petId} petName={details.petName}
        onRemove={remove} onClose={close} onRemoved={close} />;
}
